import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, OnChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { IconsService } from 'src/app/shared/services/icons.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { Campus } from 'src/app/models/campus.model';
import { Family } from 'src/app/models/family.model';
import { School } from 'src/app/models/school.model';
import { Section } from 'src/app/models/section.model';
import { Student } from 'src/app/models/student.model';
import { EscolarService } from 'src/app/services/escolar.service';
import { SecureStorageService } from 'src/app/services/secure-storage.service';

@Component({
  selector: 'app-form-student',
  template: `
    <form [formGroup]="studentForm" (ngSubmit)="onSubmit()">
      <div class="form-group">
        <label for="name" class="form-label ms-2">Nombre</label>
        <input type="text" class="form-control" id="name" formControlName="name">
        <div *ngIf="ufc['name'].touched && ufc['name'].invalid">
          <div *ngIf="ufc['name'].errors?.['required']" class="text-danger text-sm">El nombre es requerido.</div>
        </div>
      </div>
      <div class="form-group mt-4">
        <label for="p_last_name" class="form-label">Primer Apellido</label>
        <input type="text" class="form-control" id="p_last_name" formControlName="p_last_name">
        <div *ngIf="ufc['p_last_name'].touched && ufc['p_last_name'].invalid">
          <div *ngIf="ufc['p_last_name'].errors?.['required']" class="text-danger text-sm">El primer apellido es requerido.</div>
        </div>
      </div>
      <div class="form-group mt-4">
        <label for="m_last_name" class="form-label">Segundo Apellido</label>
        <input type="text" class="form-control" id="m_last_name" formControlName="m_last_name">
        <div *ngIf="ufc['m_last_name'].touched && ufc['m_last_name'].invalid">
          <div *ngIf="ufc['m_last_name'].errors?.['required']" class="text-danger text-sm">El segundo apellido es requerido.</div>
        </div>
      </div>
      <div class="form-group mt-4" *ngIf="campuses!.length > 1">
        <label for="section_id" class="form-label">Campus</label>
        <select class="form-select" formControlName="campus_id" (change)="selectCampus($event)">
          <option selected disabled value="">Selecciona un campus</option>
          <option *ngFor="let campus of campuses" [value]="campus.campus_id">{{campus.name}}</option>
        </select>
        <div *ngIf="ufc['section_id'].touched && ufc['section_id'].invalid">
          <div *ngIf="ufc['section_id'].errors?.['required']" class="text-danger text-sm">El campus es requerido.</div>
        </div>
      </div>
      <div class="form-group mt-4 w-100" *ngIf="dropdownList.length > 0">
        <label for="section_id" class="form-label">Grado</label>
        <ng-multiselect-dropdown
          [settings]="dropdownSettings"
          [data]="dropdownList"
          [(ngModel)]="selectedItems"
          (onSelect)="onItemSelect($event)"
          (onSelectAll)="onSelectAll($event)"
          class="border-secondary"
          [ngModelOptions]="{standalone: true}"
        >
        </ng-multiselect-dropdown>
        <div *ngIf="ufc['sections'].touched && ufc['sections'].invalid">
          <div *ngIf="ufc['sections'].errors?.['required']" class="text-danger text-sm">El grado es requerido.</div>
        </div>
      </div>
      <div class="from-group text-end mt-5">
        <hr>
        <button class="btn btn-link text-dark me-2" routerLink="/students">Cancelar</button>
        <button class="btn btn-escalas">Guardar</button> 
      </div>
      
    </form>
  `,
  styleUrls: []
})
export class FormStudentComponent implements OnInit, OnChanges {
  @Input() student: Student | null = null;
  @Input() school: School | undefined;
  @Input() sections: Section[] = [];
  @Input() campuses: Campus[] = [];
  @Output() studentData: EventEmitter<any> = new EventEmitter<any>();

  families: Family[] = [];

  studentForm: FormGroup = new FormGroup({});
  dropdownList: any[] = [];
  selectedItems: any[] = [];
  dropdownSettings: any = {};

  searchControl: FormControl = new FormControl();

  constructor(
    private readonly store: EscolarService,
    private readonly fb: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly escolarService: EscolarService,
    private readonly router: Router,
    public icons: IconsService,
    private readonly secureStorage: SecureStorageService
  ) {
    this.initForm();
    this.student?.sections?.map((item: any) => {
      this.dropdownList.push({ item_id: item.section_id, item_text: `${item.section} ${item.grade} ${item.group}` });
    });
  }

  ngOnInit(): void {
    this.dropdownSettings = {
      singleSelection: false,
      idField: 'item_id',
      textField: 'item_text',
      selectAllText: 'Seleccionar Todo',
      unSelectAllText: 'Deseleccionar Todo',
      itemsShowLimit: 3,
      allowSearchFilter: true
    };

    this.searchControl.valueChanges.pipe(
    debounceTime(200),
    distinctUntilChanged(),
    switchMap((term: string) => this.store.getFamilies({
      name: term, 
      campus_id: this.campuses.map((item: any) => item.campus_id)
    }))).subscribe((results: any) => {
      this.families = results.data;

    });
  }

  get ufc() {
    return this.studentForm.controls;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['student']?.currentValue) {
      const student = changes['student'].currentValue;
      this.studentForm.patchValue(student);
      // Load the student's current sections into the dropdown selection
      this.loadStudentSections(student);
    }

    if (changes['campuses']?.currentValue) {
      this.handleCampusChanges(changes['campuses'].currentValue);
    }

    if (changes['sections']?.currentValue) {
      // Clear existing dropdown list to avoid duplicates
      this.dropdownList = [];
      for (const item of changes['sections'].currentValue) {
        this.dropdownList.push({ item_id: item.section_id, item_text: `${item.section} ${item.grade} ${item.group}` });
      }
    }
  }

  private async handleCampusChanges(campuses: any[]) {
    const campusCount = campuses.length;
    
    if (campusCount === 1) {
      // If only one campus, default it
      this.studentForm.patchValue({ campus_id: campuses[0].campus_id });
      this.loadSections(campuses[0].campus_id);
    } else if (campusCount > 1) {
      // Multiple campuses - try to get from current user
      const currentUserCampusId = await this.getCurrentUserCampusId();
      if (currentUserCampusId) {
        this.studentForm.patchValue({ campus_id: currentUserCampusId });
        this.loadSections(currentUserCampusId);
      }
    }
  }

  private loadSections(campusId: number) {
    this.store.getSections({ campus_id: campusId }).subscribe((data: any) => {
      this.sections = data.data;
      // Clear dropdown list to avoid duplicates
      this.dropdownList = [];
      for (const item of this.sections) {
        this.dropdownList.push({ item_id: item.section_id, item_text: `${item.section} ${item.grade} ${item.group}` });
      }
    });
  }

  initForm() {
    this.studentForm = this.fb.group({
      student_id: [''],
      name: ['', Validators.required],
      p_last_name: ['', Validators.required],
      m_last_name: ['', Validators.required],
      sections: ['', Validators.required],
      campus_id: ['', Validators.required],
      family: [''],
    });
  }

  /**
   * Return a campus_id derived from current user or preferences stored in encrypted storage.
   * Handles different possible shapes (user.campus, user.campus_id, preferences.campus_id).
   */
  private async getCurrentUserCampusId(): Promise<number | null> {
    try {
      const userData = await this.secureStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : {};
      
      // Try from user's campus if single campus
      if (user?.campus?.campus_id) {
        return Number(user.campus.campus_id);
      }
      
      // Try from preferences
      const prefsData = await this.secureStorage.getItem('preferences');
      const prefs = prefsData ? JSON.parse(prefsData) : {};
      if (prefs?.campus_id) {
        return Number(prefs.campus_id);
      }
    } catch (error) {
      console.error('Error getting user campus from encrypted storage:', error);
    }
    return null;
  }

  loadStudentSections(student: any) {
    // Clear existing selected items to avoid duplicates
    this.selectedItems = [];
    
    if (student?.sections) {
      for (const item of student.sections) {
        // Make sure the text format matches what's used in the dropdown list
        const itemText = `${item.section || item.name} ${item.grade} ${item.group || ''}`.trim();
        this.selectedItems.push({ 
          item_id: item.section_id, 
          item_text: itemText
        });
      }
    }
  }

  selectCampus(campus: any) {
    this.escolarService.getSections({ campus_id: campus.target.value }).subscribe((data: any) => {
      this.sections = data.data;
      // Clear dropdown list and selected items when campus changes
      this.dropdownList = [];
      this.selectedItems = [];

      for (const item of this.sections) {
        this.dropdownList.push({ item_id: item.section_id, item_text: `${item.section} ${item.grade} ${item.group}` });
      }      
    });
  }

  onSubmit() {
    
    if (this.selectedItems.length > 0) {
      const selectedSections: any[] = [];
      for (const item of this.selectedItems) {
        selectedSections.push({ section_id: item.item_id, section: item.item_text });
      }
      this.studentForm.patchValue({ sections: selectedSections });
    } else {
      this.toastr.error('Por favor, selecciona al menos un grupo');
      return;
    }
    if (this.studentForm.valid) {
      this.studentData.emit(this.studentForm.value);
    } else {
      this.toastr.error('Por favor, llena todos los campos');
      this.studentForm.markAllAsTouched();
    }
  }

  onItemSelect(item: any) {
    
  }
  onSelectAll(items: any) {
    
  }

}
